import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

const initialState = {
  // Library page state
  documents: [],
  folders: [],
  folderTree: [],
  currentFolderId: null,
  breadcrumbs: [],
  pagination: {
    total: 0,
    page: 1,
    pageSize: 20,
  },
  search: '',
  loading: false,
  error: null,
  uploading: false,

  // Knowledge Center state
  knowledgeCenter: {
    documents: [],
    folders: [],
    totalDocuments: 0,
    totalFolders: 0,
    breadcrumbs: [],
    currentFolderId: null,
    loading: false,
    error: null,
  },
};

// ─── Folder Thunks ──────────────────────────────────────────────────

export const fetchFolders = createAsyncThunk(
  'library/fetchFolders',
  async ({ parentId = null, search = '' } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (parentId !== null && !search) params.append('parent_id', parentId);
      if (search) params.append('search', search);
      const response = await api.get(`/library/folders?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch folders');
    }
  }
);

export const fetchFolderTree = createAsyncThunk(
  'library/fetchFolderTree',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/library/folders/tree');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch folder tree');
    }
  }
);

export const fetchBreadcrumbs = createAsyncThunk(
  'library/fetchBreadcrumbs',
  async (folderId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/library/folders/${folderId}/breadcrumbs`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch breadcrumbs');
    }
  }
);

export const createFolder = createAsyncThunk(
  'library/createFolder',
  async ({ name, parentId }, { rejectWithValue }) => {
    try {
      const response = await api.post('/library/folders', { name, parent_id: parentId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create folder');
    }
  }
);

export const renameFolder = createAsyncThunk(
  'library/renameFolder',
  async ({ folderId, name }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/library/folders/${folderId}`, { name });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to rename folder');
    }
  }
);

export const deleteFolder = createAsyncThunk(
  'library/deleteFolder',
  async (folderId, { rejectWithValue }) => {
    try {
      await api.delete(`/library/folders/${folderId}`);
      return folderId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete folder');
    }
  }
);

// ─── Document Thunks ────────────────────────────────────────────────

export const fetchDocuments = createAsyncThunk(
  'library/fetchDocuments',
  async ({ folderId = null, search = '', page = 1, pageSize = 20, allFolders = false } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (folderId !== null) params.append('folder_id', folderId);
      if (search) params.append('search', search);
      params.append('page', page);
      params.append('page_size', pageSize);
      if (allFolders) params.append('all_folders', 'true');
      const response = await api.get(`/library/documents?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch documents');
    }
  }
);

export const uploadDocument = createAsyncThunk(
  'library/uploadDocument',
  async ({ file, folderId, description }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const params = new URLSearchParams();
      if (folderId) params.append('folder_id', folderId);
      if (description) params.append('description', description);
      const response = await api.post(`/library/documents/upload?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to upload document');
    }
  }
);

export const createDocument = createAsyncThunk(
  'library/createDocument',
  async ({ fileName, documentType, folderId, projectId, description }, { rejectWithValue }) => {
    try {
      const response = await api.post('/library/documents/create', {
        file_name: fileName,
        document_type: documentType,
        folder_id: folderId || null,
        project_id: projectId || null,
        description: description || null,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create document');
    }
  }
);

export const updateDocument = createAsyncThunk(
  'library/updateDocument',
  async ({ documentId, data }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/library/documents/${documentId}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update document');
    }
  }
);

export const deleteDocument = createAsyncThunk(
  'library/deleteDocument',
  async (documentId, { rejectWithValue }) => {
    try {
      await api.delete(`/library/documents/${documentId}`);
      return documentId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete document');
    }
  }
);

// ─── Linking Thunks ─────────────────────────────────────────────────

export const linkDocumentToProject = createAsyncThunk(
  'library/linkDocumentToProject',
  async ({ documentId, projectId }, { rejectWithValue }) => {
    try {
      const response = await api.post('/library/links/document', {
        document_id: documentId,
        project_id: projectId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to link document');
    }
  }
);

export const unlinkDocumentFromProject = createAsyncThunk(
  'library/unlinkDocumentFromProject',
  async ({ documentId, projectId }, { rejectWithValue }) => {
    try {
      await api.delete(`/library/links/document?document_id=${documentId}&project_id=${projectId}`);
      return { documentId, projectId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to unlink document');
    }
  }
);

export const linkFolderToProject = createAsyncThunk(
  'library/linkFolderToProject',
  async ({ folderId, projectId }, { rejectWithValue }) => {
    try {
      const response = await api.post('/library/links/folder', {
        folder_id: folderId,
        project_id: projectId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to link folder');
    }
  }
);

export const unlinkFolderFromProject = createAsyncThunk(
  'library/unlinkFolderFromProject',
  async ({ folderId, projectId }, { rejectWithValue }) => {
    try {
      await api.delete(`/library/links/folder?folder_id=${folderId}&project_id=${projectId}`);
      return { folderId, projectId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to unlink folder');
    }
  }
);

export const bulkLinkToProject = createAsyncThunk(
  'library/bulkLinkToProject',
  async ({ documentIds, folderIds, projectId }, { rejectWithValue }) => {
    try {
      const response = await api.post('/library/links/bulk', {
        document_ids: documentIds,
        folder_ids: folderIds,
        project_id: projectId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to link items');
    }
  }
);

// ─── Knowledge Center Thunks ────────────────────────────────────────

export const fetchKnowledgeCenter = createAsyncThunk(
  'library/fetchKnowledgeCenter',
  async ({ projectId, search = '', folderId = null }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (folderId) params.append('folder_id', folderId);
      const response = await api.get(`/projects/${projectId}/knowledge?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch knowledge center');
    }
  }
);

export const createKcFolder = createAsyncThunk(
  'library/createKcFolder',
  async ({ projectId, name, parentFolderId }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/projects/${projectId}/knowledge/folders`,
        { name, parent_id: parentFolderId || null }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create folder');
    }
  }
);

export const uploadToKnowledgeCenter = createAsyncThunk(
  'library/uploadToKnowledgeCenter',
  async ({ projectId, file, folderId, description }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const params = new URLSearchParams();
      if (folderId) params.append('folder_id', folderId);
      if (description) params.append('description', description);
      const response = await api.post(
        `/projects/${projectId}/knowledge/upload?${params.toString()}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to upload document');
    }
  }
);

// ─── Slice ──────────────────────────────────────────────────────────

const librarySlice = createSlice({
  name: 'library',
  initialState,
  reducers: {
    setCurrentFolder: (state, action) => {
      state.currentFolderId = action.payload;
    },
    setSearch: (state, action) => {
      state.search = action.payload;
    },
    clearLibrary: () => initialState,
    clearKnowledgeCenter: (state) => {
      state.knowledgeCenter = initialState.knowledgeCenter;
    },
  },
  extraReducers: (builder) => {
    // Fetch folders
    builder
      .addCase(fetchFolders.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFolders.fulfilled, (state, action) => {
        state.folders = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchFolders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch folder tree
    builder
      .addCase(fetchFolderTree.fulfilled, (state, action) => {
        state.folderTree = action.payload;
      });

    // Fetch breadcrumbs
    builder
      .addCase(fetchBreadcrumbs.fulfilled, (state, action) => {
        state.breadcrumbs = action.payload;
      });

    // Create folder
    builder
      .addCase(createFolder.fulfilled, (state, action) => {
        state.folders.push(action.payload);
      });

    // Delete folder
    builder
      .addCase(deleteFolder.fulfilled, (state, action) => {
        state.folders = state.folders.filter(f => f.id !== action.payload);
      });

    // Fetch documents
    builder
      .addCase(fetchDocuments.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.documents = action.payload.items;
        state.pagination = {
          total: action.payload.total,
          page: action.payload.page,
          pageSize: action.payload.page_size,
        };
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Upload document
    builder
      .addCase(uploadDocument.pending, (state) => {
        state.uploading = true;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.documents.unshift(action.payload);
        state.pagination.total += 1;
        state.uploading = false;
      })
      .addCase(uploadDocument.rejected, (state) => {
        state.uploading = false;
      });

    // Delete document
    builder
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.documents = state.documents.filter(d => d.id !== action.payload);
        state.pagination.total = Math.max(0, state.pagination.total - 1);
      });

    // Update document
    builder
      .addCase(updateDocument.fulfilled, (state, action) => {
        const idx = state.documents.findIndex(d => d.id === action.payload.id);
        if (idx !== -1) {
          state.documents[idx] = action.payload;
        }
      });

    // Knowledge Center
    builder
      .addCase(fetchKnowledgeCenter.pending, (state) => {
        state.knowledgeCenter.loading = true;
      })
      .addCase(fetchKnowledgeCenter.fulfilled, (state, action) => {
        state.knowledgeCenter.documents = action.payload.documents;
        state.knowledgeCenter.folders = action.payload.folders;
        state.knowledgeCenter.totalDocuments = action.payload.total_documents;
        state.knowledgeCenter.totalFolders = action.payload.total_folders;
        state.knowledgeCenter.breadcrumbs = action.payload.breadcrumbs || [];
        state.knowledgeCenter.currentFolderId = action.payload.current_folder_id || null;
        state.knowledgeCenter.loading = false;
        state.knowledgeCenter.error = null;
      })
      .addCase(fetchKnowledgeCenter.rejected, (state, action) => {
        state.knowledgeCenter.loading = false;
        state.knowledgeCenter.error = action.payload;
      });

    // Upload to knowledge center
    builder
      .addCase(uploadToKnowledgeCenter.fulfilled, (state, action) => {
        state.knowledgeCenter.documents.unshift(action.payload);
        state.knowledgeCenter.totalDocuments += 1;
      });

    // Unlink document from project (remove from knowledge center)
    builder
      .addCase(unlinkDocumentFromProject.fulfilled, (state, action) => {
        state.knowledgeCenter.documents = state.knowledgeCenter.documents.filter(
          d => d.id !== action.payload.documentId
        );
        state.knowledgeCenter.totalDocuments = Math.max(0, state.knowledgeCenter.totalDocuments - 1);
      });

    // Unlink folder from project (remove from knowledge center)
    builder
      .addCase(unlinkFolderFromProject.fulfilled, (state, action) => {
        state.knowledgeCenter.folders = state.knowledgeCenter.folders.filter(
          f => f.id !== action.payload.folderId
        );
        state.knowledgeCenter.totalFolders = Math.max(0, state.knowledgeCenter.totalFolders - 1);
      });
  },
});

export const { setCurrentFolder, setSearch, clearLibrary, clearKnowledgeCenter } = librarySlice.actions;
export default librarySlice.reducer;
